import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { Icon } from '../Icon.js';
import { useToast } from '../ui/Toast.js';
import { TRIAL_WARNING_DAYS } from '@evo-hub/shared';
import { useSubscription, useCreateSubscriptionCheckout } from '../../hooks/useSettings.js';

// ── Aviso del período de prueba (2026-08-18) ─────────────────────────────────
// UN solo componente para los tres lugares donde aparece (banda de la app,
// paso 7 de la inducción, Ajustes → Suscripción). No es cosmético: si el
// contador de días viviera en tres lugares, tarde o temprano dos de ellos
// dirían cosas distintas el mismo día — y el número que le importa al cliente
// es exactamente ese.
//
// Decide solo, sin props de estado: cada caller lo monta y el componente sabe
// si tiene algo que decir. Un caller que tenga que preguntar "¿hay prueba?"
// antes de montarlo es un caller que se puede olvidar de preguntar.

type Variant = 'band' | 'block';

/** A quién escribirle cuando la cuenta ya no deja operar. Se configura al
 *  construir (VITE_SUPPORT_EMAIL); sin valor, la línea no se muestra — una
 *  dirección inventada es peor que ninguna. */
const CONTACTO_SOPORTE: string | undefined = import.meta.env['VITE_SUPPORT_EMAIL'];

/** Nivel de urgencia → color. Los tres estados son distintos y se ven
 *  distintos: queda tiempo (cyan), se termina (ámbar), se terminó (rojo).
 *
 *  El color va en el borde y el texto, NO en el fondo: no hay token de fondo
 *  por tono en tokens.css (solo --evo-cyan-12/-18, sin equivalente para
 *  danger/warning) y inventar tres rgba a mano acá sería el hardcode que la
 *  REGLA #2 prohíbe. El fondo es la superficie de siempre. */
function toneFor(daysLeft: number, active: boolean): string {
  if (!active) return 'var(--evo-danger)';
  if (daysLeft <= TRIAL_WARNING_DAYS) return 'var(--evo-warning)';
  return 'var(--evo-cyan)';
}

export function TrialNotice({ variant = 'band' }: { variant?: Variant }) {
  const { t } = useTranslation();
  const { data } = useSubscription();
  const checkout = useCreateSubscriptionCheckout();
  const toast = useToast();

  const trial = data?.trial;
  // Una cuenta que ya paga no necesita ver nada de esto, aunque haya tenido
  // prueba: el aviso dejó de aplicar en el momento en que pagó.
  if (!trial || !data || data.status === 'active' || data.billingExempt) return null;

  const tone = toneFor(trial.daysLeft, trial.active);
  const days = Math.max(trial.daysLeft, 0);

  const message = trial.active
    ? t('subscription.trial.daysLeft', { count: days, defaultValue: 'Te quedan {{count}} días de prueba' })
    : t('subscription.trial.expired', 'Tu prueba terminó — la cuenta está en solo lectura');

  // Sin precio de excepción hay que ELEGIR un plan, y esta banda no es el lugar
  // para eso. Va a /activar, que es la misma pantalla a la que llevan los
  // correos: si hay un solo plan con precio dispara el checkout sola, y si hay
  // varios manda al selector. Antes esto mandaba directo a Ajustes, así que el
  // camino desde el correo y el camino desde la banda terminaban en lugares
  // distintos — y el de la banda pedía un clic más para lo mismo.
  function goToCheckout() {
    if (!data?.priceId) { window.location.href = '/activar'; return; }
    checkout.mutate(undefined, {
      onSuccess: r => { window.location.href = r.url; },
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)',
        padding: variant === 'band'
          ? 'var(--evo-space-2) var(--evo-content-pad-x)'
          : 'var(--evo-space-3) var(--evo-space-4)',
        background: 'var(--evo-bg-2)',
        // Filete de color a la izquierda: marca la urgencia sin teñir el fondo.
        borderLeft: `2px solid ${tone}`,
        borderBottom: variant === 'band' ? '1px solid var(--evo-stroke)' : undefined,
        border: variant === 'block' ? '1px solid var(--evo-stroke)' : undefined,
        borderLeftColor: tone,
        borderLeftWidth: '2px',
        borderLeftStyle: 'solid',
        borderRadius: variant === 'block' ? 'var(--evo-r)' : undefined,
      }}
    >
      <span style={{ color: tone, flexShrink: 0, display: 'inline-flex' }}>
        <Icon name="alert" size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: tone, fontWeight: 'var(--evo-fw-semibold)' }}>
          {message}
        </span>
        {/* Qué pasa después, siempre a la vista. "Solo lectura" sin decir que
            no se borra nada es la frase que genera la llamada de las 11pm. */}
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
          {trial.active
            ? t('subscription.trial.whatHappens', 'Después la cuenta pasa a solo lectura. No se borra nada y puedes activarla cuando quieras.')
            : t('subscription.trial.whatHappensExpired', 'Puedes ver y descargar todo. Activa la suscripción para volver a operar.')}
        </span>
        {/* El contacto aparece solo cuando ya venció. Mientras la prueba corre
            no hay nada que consultar; el día que deja de poder escribir, sí —
            y no tener a quién escribirle es cuando alguien se va sin avisar. */}
        {!trial.active && CONTACTO_SOPORTE && (
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)' }}>
            {t('subscription.trial.contact', '¿Dudas o algo que no cierra? Escríbenos a ')}
            <a href={`mailto:${CONTACTO_SOPORTE}`} style={{ color: 'var(--evo-cyan)' }}>{CONTACTO_SOPORTE}</a>
          </span>
        )}
      </div>
      <Button
        variant={trial.active && trial.daysLeft > TRIAL_WARNING_DAYS ? 'outline' : 'solid'}
        tone="cyan"
        size="sm"
        loading={checkout.isPending}
        onClick={goToCheckout}
      >
        {t('subscription.trial.activateBtn', 'Activar suscripción')}
      </Button>
    </div>
  );
}
